import { useState, useEffect } from 'react';
import { Wrench, Loader2, AlertTriangle, CheckCircle2, Search, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { formatDate } from '@/utils/dateFormat';
import { posAPI, storesAPI } from '@/services/api';

// HQ tool: bills billed before per-sale FIFO stock deduction existed can be re-applied (or flagged as already reflected).
export default function SalesStockRepairPage() {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [tab, setTab] = useState('products');
  const [confirm, setConfirm] = useState(null); // 'apply' | 'mark'
  const [note, setNote] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { storesAPI.list({ limit: 500 }).then(r => { const l = r.data?.items || r.data || []; setStores(l); if (l[0]) setStoreId(l[0].id); }).catch(() => {}); }, []);

  const load = async () => {
    if (!storeId) return;
    setLoading(true); setSelected({});
    try { setData((await posAPI.stockRepairPending({ store_id: storeId, date_from: from || undefined, date_to: to || undefined })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (storeId) load(); }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selIds = Object.keys(selected).filter(k => selected[k]);
  const scope = selIds.length ? { sale_ids: selIds } : { date_from: from || undefined, date_to: to || undefined };
  const scopeLabel = selIds.length ? `${selIds.length} selected bill(s)` : `all ${data?.total_pending || 0} pending bill(s)${from || to ? ' in the date range' : ''}`;

  const run = async () => {
    setBusy(true);
    try {
      const body = { store_id: storeId, note: note || undefined, ...scope };
      if (confirm === 'apply') { const r = (await posAPI.stockRepairApply(body)).data; toast.success(`${r.bills_fixed} bill(s) fixed · ${r.units_deducted} units deducted${r.lines_short ? ` · ${r.lines_short} line(s) had no stock left (recorded as short)` : ''}`); }
      else { const r = (await posAPI.stockRepairMarkDone(body)).data; toast.success(`${r.bills_marked} bill(s) marked as already reflected in stock`); }
      setConfirm(null); setNote(''); setTyped(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const store = stores.find(s => s.id === storeId);
  return (
    <div className="p-6 space-y-4" data-testid="sales-stock-repair-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Wrench className="w-6 h-6" /> Sales Stock Repair</h1>
        <p className="text-sm text-gray-500 max-w-3xl">Bills billed before per-sale stock deduction was fixed may not have reduced store stock. Review the impact per product, then either <b>re-apply the deduction</b> (FIFO, never below zero) or <b>mark the bills as already reflected</b> if a physical stock audit was approved after them.</p>
      </div>

      <Card><CardContent className="p-3 flex flex-wrap gap-3 items-end">
        <div><Label className="text-xs">Store</Label>
          <Select value={storeId} onValueChange={setStoreId}><SelectTrigger className="w-60" data-testid="repair-store"><SelectValue placeholder="Store" /></SelectTrigger>
            <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.code} · {s.name}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="w-40" data-testid="repair-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} className="w-40" data-testid="repair-to" /></div>
        <Button onClick={load} disabled={loading || !storeId} data-testid="repair-load">{loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />} Check</Button>
        <div className="flex-1" />
        {data && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setConfirm('mark')} disabled={!data.total_pending} data-testid="repair-mark-open"><ShieldCheck className="w-4 h-4 mr-1" /> Mark as already reflected</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={() => setConfirm('apply')} disabled={!data.total_pending} data-testid="repair-apply-open"><Wrench className="w-4 h-4 mr-1" /> Re-apply deduction</Button>
          </div>
        )}
      </CardContent></Card>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[['Pending bills', data.total_pending, 'text-rose-700'], ['Products affected', data.products.length, ''], ['Units not deducted', data.products.reduce((a, p) => a + p.sold_qty, 0), ''], ['Bills fixed so far', data.fixed_so_far, 'text-emerald-700']].map(([t, v, c]) => (
              <div key={t} className="rounded-lg border bg-white p-3"><div className="text-[11px] text-gray-500">{t}</div><div className={`text-xl font-bold ${c}`} data-testid={`repair-stat-${t.toLowerCase().replace(/ /g, '-')}`}>{v}</div></div>
            ))}
          </div>
          {data.total_pending === 0 && <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 text-sm flex items-center gap-2" data-testid="repair-clean"><CheckCircle2 className="w-4 h-4" /> Every bill of {store?.code} in this range has its stock deduction recorded. Nothing to repair.</div>}

          <div className="flex gap-1 border-b">
            {[['products', `By product (${data.products.length})`], ['bills', `Bills (${data.returned}${data.total_pending > data.returned ? ` of ${data.total_pending}` : ''})`]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === k ? 'border-gray-900 font-semibold' : 'border-transparent text-gray-500'}`} data-testid={`repair-tab-${k}`}>{l}</button>
            ))}
          </div>

          {tab === 'products' && (
            <Card><CardContent className="p-0">
              <table className="w-full text-sm" data-testid="repair-products">
                <thead className="bg-gray-50 text-xs text-gray-500"><tr><th className="p-2 text-left">Product</th><th className="p-2 text-right">Sold (not deducted)</th><th className="p-2 text-right">Bills</th><th className="p-2 text-right">Current stock</th><th className="p-2 text-right">Stock after re-apply</th><th className="p-2 text-left">Note</th></tr></thead>
                <tbody>
                  {data.products.map(p => (
                    <tr key={p.product_id} className="border-t" data-testid={`repair-product-${p.sku}`}>
                      <td className="p-2"><div className="font-medium">{p.product_name}</div><div className="text-xs text-gray-500">{p.sku}</div></td>
                      <td className="p-2 text-right font-mono text-rose-700">{p.sold_qty}</td>
                      <td className="p-2 text-right">{p.sales}</td>
                      <td className="p-2 text-right font-mono">{p.current_stock}</td>
                      <td className="p-2 text-right font-mono font-semibold">{p.after_reapply}</td>
                      <td className="p-2 text-xs">{p.would_go_short > 0 ? <Badge className="bg-amber-100 text-amber-800"><AlertTriangle className="w-3 h-3 mr-1" /> {p.would_go_short} more than stock — probably already deducted or sold from unrecorded stock</Badge> : <span className="text-gray-400">ok</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          )}

          {tab === 'bills' && (
            <Card><CardContent className="p-0">
              <table className="w-full text-sm" data-testid="repair-bills">
                <thead className="bg-gray-50 text-xs text-gray-500"><tr>
                  <th className="p-2 w-8"><Checkbox checked={selIds.length > 0 && selIds.length === data.sales.length} onCheckedChange={(c) => setSelected(c ? Object.fromEntries(data.sales.map(s => [s.id, true])) : {})} data-testid="repair-select-all" /></th>
                  <th className="p-2 text-left">Bill</th><th className="p-2 text-left">Date</th><th className="p-2 text-left">Items</th><th className="p-2 text-right">Total</th><th className="p-2 text-left">Cashier</th></tr></thead>
                <tbody>
                  {data.sales.map(s => (
                    <tr key={s.id} className="border-t" data-testid={`repair-bill-${s.bill_number}`}>
                      <td className="p-2"><Checkbox checked={!!selected[s.id]} onCheckedChange={(c) => setSelected(x => ({ ...x, [s.id]: !!c }))} data-testid={`repair-check-${s.bill_number}`} /></td>
                      <td className="p-2 font-mono text-xs">{s.bill_number}</td>
                      <td className="p-2 text-xs">{formatDate(s.bill_datetime)}</td>
                      <td className="p-2 text-xs">{(s.items || []).map(i => `${i.product_name || i.sku} ×${i.quantity}`).join(', ')}</td>
                      <td className="p-2 text-right">₹{Number(s.grand_total || 0).toFixed(0)}</td>
                      <td className="p-2 text-xs">{s.cashier_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          )}
        </>
      )}

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="repair-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2">{confirm === 'apply' ? <><Wrench className="w-5 h-5 text-rose-600" /> Re-apply stock deduction</> : <><ShieldCheck className="w-5 h-5" /> Mark as already reflected</>}</DialogTitle></DialogHeader>
          <div className="text-sm space-y-2">
            <p>Scope: <b>{store?.code}</b> · {scopeLabel}.</p>
            {confirm === 'apply' && !selIds.length && (
              <div className="rounded border border-amber-300 bg-amber-50 p-2 text-amber-900 text-xs" data-testid="repair-bulk-warning">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> This deducts stock for <b>every</b> pending bill. Type the store code <b>{store?.code}</b> to confirm:
                <Input value={typed} onChange={e => setTyped(e.target.value)} placeholder={store?.code} className="h-8 mt-1 bg-white" data-testid="repair-bulk-typed" />
              </div>
            )}
            {confirm === 'apply'
              ? <p className="text-gray-600">Each bill's sold quantities are deducted from the store's stock rows (oldest first). Stock never goes below zero — any shortfall is recorded on the bill. A movement row is written per line so this is fully traceable. Bills already deducted are skipped automatically.</p>
              : <p className="text-gray-600">No stock changes. Use this when a physical stock audit was approved after these bills, so their sales are already reflected in the counted stock.</p>}
            <div><Label>Note (optional)</Label><Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Verified with store manager before repair" data-testid="repair-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={busy} data-testid="repair-cancel">Cancel</Button>
            <Button onClick={run} disabled={busy || (confirm === 'apply' && !selIds.length && typed.trim().toUpperCase() !== (store?.code || '').toUpperCase())} className={confirm === 'apply' ? 'bg-rose-600 hover:bg-rose-700' : ''} data-testid="repair-confirm">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{confirm === 'apply' ? 'Deduct now' : 'Mark done'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
